import React, { useState, useEffect } from 'react';
import { HotbarConfig, HotbarSlot, HotbarSlotItem } from '../game/types';
import { useGlobalTooltip } from './GlobalTooltip';
import './Hotbar.css';

interface HotbarProps {
  config: HotbarConfig;
  onSlotUpdate: (slotIndex: number, item: HotbarSlotItem | null, sourceSlotIndex?: number) => void;
  onSlotUse: (slotIndex: number) => void;
  inventory: {
    items: Array<{
      itemId: string;
      quantity: number;
      definition?: {
        id: string;
        name: string;
        icon: string;
        description?: string;
        type?: string;
        rarity?: string;
      } | null;
    }>;
  };
  onVisibilityToggle?: (visible: boolean) => void;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  slotIndex: number | null;
}

export const Hotbar: React.FC<HotbarProps> = ({ config, onSlotUpdate, onSlotUse, inventory, onVisibilityToggle }) => {
   const { showTooltip, hideTooltip } = useGlobalTooltip();
   const [now, setNow] = useState<number>(Date.now());
   const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, slotIndex: null });

  // Debug: Log when config changes
  useEffect(() => {
    console.log('[HOTBAR RENDER] Config slots:', config.slots.length, config.slots.map(s => ({ index: s.slotIndex, item: s.item?.itemId || 'empty' })));
  }, [config]);
  const [activatedSlot, setActivatedSlot] = useState<number | null>(null);
  const [dragWasHandled, setDragWasHandled] = useState<boolean>(false);
  const [lastUseTime, setLastUseTime] = useState<number>(0); // Track last hotbar use to prevent double-presses

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (): void => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, slotIndex: null });
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [contextMenu.visible]);

  // Handle number keys (1-5) for slots and E for signature move
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Handle E key for signature move (slot 0) - enter selection mode instead of immediate cast
      if (e.key === 'e' || e.key === 'E') {
        const slotIndex = 0;
        const slot = config.slots[slotIndex];
        
        // Add client-side cooldown to prevent double-presses (500ms)
        const timeSinceLastUse = Date.now() - lastUseTime;
        const CLIENT_COOLDOWN_MS = 500;
        
        if (slot && slot.item && slot.cooldownUntil <= now && timeSinceLastUse >= CLIENT_COOLDOWN_MS) {
          // Update last use time immediately to prevent double-presses
          setLastUseTime(Date.now());
          
          // Show visual feedback
          setActivatedSlot(slotIndex);
          setTimeout(() => setActivatedSlot(null), 300);
          
          // Emit event to enter signature cast mode (tile selection) instead of using immediately
          window.dispatchEvent(new CustomEvent('wildcard:enter-cast-mode', { 
            detail: { slotIndex } 
          }));
        } else if (slot && slot.item && timeSinceLastUse < CLIENT_COOLDOWN_MS) {
          // Show brief "blocked" feedback for double-press prevention
          setActivatedSlot(slotIndex);
          setTimeout(() => setActivatedSlot(null), 100);
        }
      }
      
      // Handle number keys 1-5 for hotbar slots
      const slotNum = parseInt(e.key, 10);
      if (slotNum >= 1 && slotNum <= 5) {
        const slotIndex = slotNum;
        const slot = config.slots[slotIndex];
        
        // Add client-side cooldown to prevent double-presses (500ms)
        const timeSinceLastUse = Date.now() - lastUseTime;
        const CLIENT_COOLDOWN_MS = 500;
        
        if (slot && slot.item && slot.cooldownUntil <= now && timeSinceLastUse >= CLIENT_COOLDOWN_MS) {
          // Update last use time immediately to prevent double-presses
          setLastUseTime(Date.now());
          
          // Show visual feedback
          setActivatedSlot(slotIndex);
          setTimeout(() => setActivatedSlot(null), 300);
          
          // Use the slot
          onSlotUse(slotIndex);
        } else if (slot && slot.item && timeSinceLastUse < CLIENT_COOLDOWN_MS) {
          // Show brief "blocked" feedback for double-press prevention
          setActivatedSlot(slotIndex);
          setTimeout(() => setActivatedSlot(null), 100);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onVisibilityToggle, config.slots, now, onSlotUse, lastUseTime]);

  const handleDragStart = (e: React.DragEvent, slotIndex: number) => {
    const slot = config.slots[slotIndex];
    if (!slot.item) {
      e.preventDefault();
      return;
    }
    
    setDragWasHandled(false);
    
    const payload = JSON.stringify({
      itemId: slot.item.itemId,
      fromHotbarSlot: slotIndex,
      type: 'hotbar',
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', payload);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = (e: React.DragEvent, targetSlotIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const payload: string = e.dataTransfer.getData('text/plain');
    if (!payload) return;

    try {
      const parsed = JSON.parse(payload) as { itemId?: string; fromIndex?: number; fromHotbarSlot?: number; type?: string };
      if (!parsed.itemId) return;

      // Handle drag from hotbar slot to hotbar slot
      if (parsed.type === 'hotbar' && parsed.fromHotbarSlot !== undefined) {
        const sourceSlotIndex = parsed.fromHotbarSlot;
        if (sourceSlotIndex === targetSlotIndex) return;
        
        const sourceSlot = config.slots[sourceSlotIndex];
        if (!sourceSlot.item) return;

        // Mark drag as handled so dragend doesn't remove it
        setDragWasHandled(true);

        // Use atomic move operation - pass sourceSlotIndex to indicate slot-to-slot move
        onSlotUpdate(targetSlotIndex, sourceSlot.item, sourceSlotIndex);
        return;
      }

      // Handle drag from inventory
      const invItem = inventory.items.find((i) => i.itemId === parsed.itemId);
      const defType: string | undefined = invItem?.definition?.type;

      // Only allow consumables in hotbar right now.
      if (defType !== 'consumable') {
        return;
      }

      const newItem: HotbarSlotItem = {
        itemId: parsed.itemId,
        quantity: invItem?.quantity ?? 1,
        type: 'consumable',
      };

      setDragWasHandled(true);
      onSlotUpdate(targetSlotIndex, newItem);
    } catch {
      return;
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Handle drop outside of slots (remove from hotbar)
    const payload: string = e.dataTransfer.getData('text/plain');
    if (!payload) return;

    try {
      const parsed = JSON.parse(payload) as { fromHotbarSlot?: number; type?: string };
      
      // Only handle hotbar drags that were dropped outside
      if (parsed.type === 'hotbar' && parsed.fromHotbarSlot !== undefined) {
        // If drag wasn't handled by a drop handler, remove from hotbar
        if (!dragWasHandled) {
          // Remove from source slot (no sourceSlotIndex means regular remove, not a move)
          onSlotUpdate(parsed.fromHotbarSlot, null);
        }
      }
    } catch {
      return;
    } finally {
      // Reset drag state
      setDragWasHandled(false);
    }
  };

  const handleSlotClick = (slotIndex: number) => {
    const slot = config.slots[slotIndex];
    
    // Add client-side cooldown to prevent double-clicks (500ms)
    const timeSinceLastUse = Date.now() - lastUseTime;
    const CLIENT_COOLDOWN_MS = 500;
    
    if (slot && slot.item && slot.cooldownUntil <= now && timeSinceLastUse >= CLIENT_COOLDOWN_MS) {
      // Update last use time immediately to prevent double-clicks
      setLastUseTime(Date.now());
      
      // Show visual feedback
      setActivatedSlot(slotIndex);
      setTimeout(() => setActivatedSlot(null), 300);
      
      onSlotUse(slotIndex);
    } else if (slot && slot.item && timeSinceLastUse < CLIENT_COOLDOWN_MS) {
      // Show brief "blocked" feedback for double-click prevention
      setActivatedSlot(slotIndex);
      setTimeout(() => setActivatedSlot(null), 100);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, slotIndex: number): void => {
    e.preventDefault();
    e.stopPropagation();
    
    const slot = config.slots[slotIndex];
    if (!slot.item) return;
    
    // Clamp context menu position to viewport
    const menuWidth = 180;
    const menuHeight = 180;
    let x = e.clientX;
    let y = e.clientY;
    
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 8;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }
    
    setContextMenu({ visible: true, x, y, slotIndex });
  };

    const getItemDefinition = (itemId: string, itemType?: string): { name: string; icon: string; color?: string } | null => {
      // Handle wildcard powers - they don't exist in inventory but are in hotbar
      if (itemType === 'wildcard') {
       // Map signature move IDs to element colors for visual distinction
       const elementColorMap: Record<string, string> = {
         flame: '#ff6b35',
         storm: '#00bfff',
         tide: '#1e90ff',
         shadow: '#4b0082',
         iron: '#708090',
         spirit: '#9932cc',
       };
       
      // Extract element from move ID (e.g., 'flame_burst' -> 'flame')
        const element = itemId.split('_')[0];
        const color = elementColorMap[element] || '#4fc3f7';
        
        // Format the wildcard power name for display
       const name = itemId
         .split('_')
         .map(word => word.charAt(0).toUpperCase() + word.slice(1))
         .join(' ');
       
       return {
         name,
         icon: '/assets/items/consumables/item_potion_health.png',
         color,
       };
     }
     
     const invItem = inventory.items.find((i) => i.itemId === itemId);
     return invItem?.definition && invItem.definition.name && invItem.definition.icon ? invItem.definition : null;
   };

  const getCooldownText = (slot: HotbarSlot): string => {
    if (slot.cooldownUntil <= now) return '';
    const remaining = Math.ceil((slot.cooldownUntil - now) / 1000);
    return remaining > 0 ? `${remaining}s` : '';
  };

   const handleSlotMouseEnter = (e: React.MouseEvent, slotIndex: number): void => {
     const slot = config.slots[slotIndex];
     if (!slot.item) return;
     
     const item = slot.item ? getItemDefinition(slot.item.itemId, slot.item.type) : null;
     if (!item) return;

    const invItem = inventory.items.find(i => i.itemId === slot.item!.itemId);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    let nextX = rect.left - 270;
    if (nextX < 8) {
      nextX = rect.right + 8;
    }
    let nextY = rect.top - 50;
    if (nextY < 8) {
      nextY = rect.bottom + 8;
    }

     showTooltip(
       {
         title: item.name,
         type: slot.item?.type === 'wildcard' ? 'wildcard' : (invItem?.definition?.type || 'item'),
         quantity: invItem?.quantity || slot.item.quantity,
         rarity: invItem?.definition?.rarity,
         description: invItem?.definition?.description,
         hint: slot.item?.type === 'wildcard' ? 'Press E to enter cast mode | Right-click for options' : 'Click to use | Right-click for options',
      },
      nextX,
      nextY
    );
  };

  const handleSlotMouseLeave = (): void => {
    hideTooltip();
  };

   return (
     <div className="hotbar-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
       <div className="hotbar-hint" style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Press H to toggle</div>
       <div className="hotbar">
         {config.slots.map((slot) => {
           const item = slot.item ? getItemDefinition(slot.item.itemId, slot.item.type) : null;
           const isOnCooldown = slot.cooldownUntil > now;
           const isSignatureSlot = slot.slotIndex === 0;
           
           // Render signature slot even if empty for UI consistency
           // But hide it if no signature move is equipped
           if (isSignatureSlot && !slot.item) {
             return null;
           }

           return (
             <div
               key={slot.slotIndex}
               className={`hotbar-slot ${isOnCooldown ? 'cooldown' : ''} ${activatedSlot === slot.slotIndex ? 'activated' : ''} ${slot.item?.type === 'wildcard' ? 'wildcard-slot' : ''}`}
              draggable={slot.item ? true : false}
              onDragStart={(e) => handleDragStart(e, slot.slotIndex)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDropOnSlot(e, slot.slotIndex)}
              onClick={() => handleSlotClick(slot.slotIndex)}
              onContextMenu={(e) => handleContextMenu(e, slot.slotIndex)}
              onMouseEnter={(e) => handleSlotMouseEnter(e, slot.slotIndex)}
              onMouseLeave={handleSlotMouseLeave}
               title={item ? item.name : 'Empty slot'}
               style={slot.item?.type === 'wildcard' && item?.color ? { borderColor: item.color, boxShadow: `0 0 8px ${item.color}40` } : undefined}
            >
               {slot.item && item && (
                 <>
                   <div className="slot-icon" style={{ backgroundImage: `url(${item.icon})`, backgroundColor: slot.item.type === 'wildcard' ? (item.color || '#4fc3f7') + '20' : undefined }} />
                  {slot.item.type === 'consumable' && slot.item.quantity > 0 && (
                    <div className="slot-quantity">{slot.item.quantity}</div>
                  )}
                  {isOnCooldown && (
                    <>
                      <svg className="slot-cooldown-radial" viewBox="0 0 50 50">
                        <circle cx="25" cy="25" r="23" className="cooldown-bg" />
                        <circle
                          cx="25"
                          cy="25"
                          r="23"
                          className="cooldown-progress"
                          style={{
                            strokeDasharray: `${(1 - (slot.cooldownUntil - now) / (slot.cooldownUntil - (slot.cooldownUntil - (slot.cooldownUntil - now)))) * 144.51} 144.51`,
                          }}
                        />
                      </svg>
                      <div className="slot-cooldown-text">{getCooldownText(slot)}</div>
                    </>
                  )}
                </>
              )}
              <div className="slot-number">{isSignatureSlot ? 'E' : slot.slotIndex}</div>
            </div>
          );
        })}
      </div>

      {contextMenu.visible && contextMenu.slotIndex !== null && config.slots[contextMenu.slotIndex].item && (
        <div
          className="hotbar-context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 2000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const slot = config.slots[contextMenu.slotIndex!];
            const item = slot.item ? inventory.items.find(i => i.itemId === slot.item!.itemId) : null;
            const name = item?.definition?.name || 'Unknown Item';
            const type = item?.definition?.type || 'item';
            
            return (
              <>
                <div className="hotbar-context-menu-header">{name}</div>
                <div className="hotbar-context-menu-type">{type.toUpperCase()}</div>

                <button
                  className="hotbar-context-menu-item"
                  disabled={!slot.item || slot.cooldownUntil > now}
                  onClick={() => {
                    onSlotUse(contextMenu.slotIndex as number);
                    setContextMenu({ visible: false, x: 0, y: 0, slotIndex: null });
                  }}
                >
                  Use
                </button>

                <button
                  className="hotbar-context-menu-item"
                  disabled={!slot.item}
                  onClick={() => {
                    onSlotUpdate(contextMenu.slotIndex as number, null);
                    setContextMenu({ visible: false, x: 0, y: 0, slotIndex: null });
                  }}
                >
                  Remove
                </button>

                <button
                  className="hotbar-context-menu-item"
                  onClick={() => setContextMenu({ visible: false, x: 0, y: 0, slotIndex: null })}
                >
                  Cancel
                </button>
              </>
            );
          })()}
        </div>
      )}

    </div>
  );
};
